
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  User, 
  ChevronUp, 
  ChevronDown, 
  UserCircle2, 
  LifeBuoy, 
  Settings, 
  LogOut,
  Bell
} from 'lucide-react';
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import HubspotIcon from '../icons/HubspotIcon';
import Logs from '../icons/Logs';
import { NotificationBadge } from "@/components/workspace/NotificationBadge";

interface UserProfileSectionProps {
  className?: string;
}

const UserProfileSection: React.FC<UserProfileSectionProps> = ({ className }) => {
  const [userMenuExpanded, setUserMenuExpanded] = useState(false);
  const { user, isAdmin, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isNotificationsActive = location.pathname === '/workspace/notifications-center';

  if (!session) return null;

  const toggleUserMenu = () => {
    setUserMenuExpanded(!userMenuExpanded);
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      navigate('/auth');
      toast({
        title: "Sesión cerrada",
        description: "Has cerrado sesión correctamente",
      });
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo cerrar la sesión. Inténtalo de nuevo.",
      });
    }
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setUserMenuExpanded(false);
  };

  const handleSupportClick = () => {
    navigate('/support');
    setUserMenuExpanded(false);
  };

  const handleHubspotClick = () => {
    if (user?.hubspot_portal_id) {
      window.open(`https://app.hubspot.com/contacts/${user.hubspot_portal_id}`, '_blank');
    } else {
      window.open('https://app.hubspot.com', '_blank');
    }
    setUserMenuExpanded(false);
  };

  const handleAdminClick = () => {
    navigate('/admin');
    setUserMenuExpanded(false);
  };

  const handleNotificationsClick = () => {
    navigate('/workspace/notifications-center');
    setUserMenuExpanded(false);
  };

  return (
    <div className={className}>
      <div className="px-4 py-3 border-t border-gray-200">
        <button 
          className={`flex items-center relative w-full py-2 px-3 rounded-md hover:bg-gray-100 ${isNotificationsActive ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
          onClick={handleNotificationsClick}
        >
          <div className={`w-6 h-6 ${isNotificationsActive ? 'text-blue-600' : 'text-gray-500'} mr-3 flex items-center justify-center relative`}>
            <Bell className="h-5 w-5" />
            <NotificationBadge className="top-0 right-0" />
          </div>
          <span className={`text-sm ${isNotificationsActive ? 'font-medium' : ''}`}>Mis Notificaciones</span>
        </button>
      </div>
      
      {userMenuExpanded && (
        <div className="mb-2 bg-white rounded-md shadow-md border border-gray-200">
          <div className="p-2 space-y-1">
            <button
              className="flex items-center w-full p-2 hover:bg-gray-100 rounded-md transition-colors text-sm text-left"
              onClick={handleProfileClick}
            >
              <UserCircle2 className="h-4 w-4 mr-2 text-gray-500" />
              <span>Mi perfil</span>
            </button>
            
            {user?.hubspot_portal_id && (
              <button
                className="flex items-center w-full p-2 hover:bg-gray-100 rounded-md transition-colors text-sm text-left"
                onClick={handleHubspotClick}
              >
                <HubspotIcon className="h-4 w-4 mr-2 text-gray-500" />
                <span>Abrir HubSpot</span>
              </button>
            )}
            
            <button
              className="flex items-center w-full p-2 hover:bg-gray-100 rounded-md transition-colors text-sm text-left"
              onClick={handleSupportClick}
            >
              <LifeBuoy className="h-4 w-4 mr-2 text-gray-500" />
              <span>Soporte</span>
            </button>
            
            {isAdmin && (
              <button
                className="flex items-center w-full p-2 hover:bg-gray-100 rounded-md transition-colors text-sm text-left"
                onClick={handleAdminClick}
              >
                <Settings className="h-4 w-4 mr-2 text-gray-500" />
                <span>Panel de administración</span>
              </button>
            )}
            
            <Separator className="my-2" />
            
            <button
              className="flex items-center w-full p-2 hover:bg-red-100 rounded-md transition-colors text-sm text-left text-red-500 hover:text-red-600"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      )}

      <div 
        className={`flex items-center cursor-pointer p-2 hover:bg-gray-100 rounded-md transition-colors ${
          userMenuExpanded ? 'bg-gray-100' : ''
        }`}
        onClick={toggleUserMenu}
      >
        <div className="w-8 h-8 bg-[#2E80EC] text-white rounded-full flex items-center justify-center mr-2 flex-shrink-0">
          <User className="h-5 w-5" />
        </div>
        <div className="flex-1 text-sm">
          <div className="font-medium">{user?.user_metadata?.first_name || 'Usuario'}</div>
          <div className="text-xs text-gray-500">{user?.email}</div>
        </div>
        {userMenuExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        )}
      </div>
    </div>
  );
};

export default UserProfileSection;
